'use client';

import './index.css';

import { storyblokEditable } from '@storyblok/react';
import classnames from 'classnames';
import { useCallback, useState } from 'react';
import AnimateHeight from 'react-animate-height';

import { Button } from '@/components/button';
import * as ICONS from '@/components/icons';
import { Image } from '@/components/image';
import { MaxWidth } from '@/components/max-width';

import { isImage } from '@/helpers/image';
import { hasText, render } from '@/helpers/storyblok/rich-text';
import { markdownToHtml } from '@/helpers/string';

const COLLAPSED_ITEMS_COUNT = 3;

const ProgramsCard = ({ item }) => {
  const theme = item.theme || 'standard';
  const subItems = item.items || [];
  const visibleItems = subItems.slice(0, COLLAPSED_ITEMS_COUNT);
  const hiddenItems = subItems.slice(COLLAPSED_ITEMS_COUNT);
  const hasHidden = hiddenItems.length > 0;

  const [open, setOpen] = useState(false);

  const toggle = useCallback(() => {
    setOpen((state) => !state);
  }, []);

  return (
    <li
      {...storyblokEditable(item)}
      className={classnames(
        'programs__card',
        `programs__card--theme-${theme}`,
        {
          'programs__card--open': open,
        },
      )}
    >
      <header className="programs__card__header">
        {item.pill ? (
          <span className="programs__card__pill">
            <span className="programs__card__pill__dot" aria-hidden="true" />

            <span className="programs__card__pill__text">{item.pill}</span>
          </span>
        ) : null}

        {item.title ? (
          <h3
            className="programs__card__title"
            dangerouslySetInnerHTML={{
              __html: markdownToHtml(item.title),
            }}
          />
        ) : null}

        {hasText(item.description) ? (
          <div className="programs__card__description">
            {render(item.description)}
          </div>
        ) : null}
      </header>

      <div className="programs__card__body">
        <div className="programs__card__top">
          {item.subPill ? (
            <span className="programs__card__sub-pill">{item.subPill}</span>
          ) : null}

          {subItems.length > 0 ? (
            <div className="programs__card__items-wrap">
              <ul className="programs__card__items">
                {visibleItems.map((sub) => (
                  <li
                    key={sub._uid}
                    {...storyblokEditable(sub)}
                    className="programs__card__item"
                  >
                    <span
                      className="programs__card__item__bullet"
                      aria-hidden="true"
                    >
                      <ICONS.CheckMark />
                    </span>

                    <div className="programs__card__item__content">
                      {render(sub.content)}
                    </div>
                  </li>
                ))}
              </ul>

              {hasHidden ? (
                <AnimateHeight
                  duration={300}
                  height={open ? 'auto' : 0}
                  className="programs__card__items-hidden"
                >
                  <ul className="programs__card__items programs__card__items--hidden">
                    {hiddenItems.map((sub) => (
                      <li
                        key={sub._uid}
                        {...storyblokEditable(sub)}
                        className="programs__card__item"
                      >
                        <span
                          className="programs__card__item__bullet"
                          aria-hidden="true"
                        >
                          <ICONS.CheckMark />
                        </span>

                        <div className="programs__card__item__content">
                          {render(sub.content)}
                        </div>
                      </li>
                    ))}
                  </ul>
                </AnimateHeight>
              ) : null}
            </div>
          ) : null}

          {hasHidden ? (
            <button
              type="button"
              className="programs__card__see-more"
              onClick={toggle}
              aria-expanded={open}
            >
              <span className="programs__card__see-more__text">
                {open ? 'Voir moins' : item.seeMore || 'Voir plus'}
              </span>

              <span
                className="programs__card__see-more__icon"
                aria-hidden="true"
              >
                <ICONS.ChevronDown />
              </span>
            </button>
          ) : null}
        </div>

        {item.cta?.length > 0 ? (
          <div className="programs__card__cta">
            {item.cta.map((cta) => (
              <Button
                key={cta._uid}
                link={cta.link}
                theme={cta.theme || 'primary'}
                height="small"
                dangerouslySetInnerHTML={{
                  __html: markdownToHtml(cta.title),
                }}
              />
            ))}
          </div>
        ) : null}
      </div>
    </li>
  );
};

const ProgramsFeaturedCard = ({ item }) => {
  const subItems = item.items || [];

  return (
    <li
      {...storyblokEditable(item)}
      className="programs__card programs__card--featured"
    >
      <div className="programs__card__featured-content">
        <div className="programs__card__featured-header">
          {item.pill ? (
            <span className="programs__card__pill">
              <span className="programs__card__pill__dot" aria-hidden="true" />

              <span className="programs__card__pill__text">{item.pill}</span>
            </span>
          ) : null}

          <div className="programs__card__featured-text">
            {item.title ? (
              <h3
                className="programs__card__title"
                dangerouslySetInnerHTML={{
                  __html: markdownToHtml(item.title),
                }}
              />
            ) : null}

            {hasText(item.description) ? (
              <div className="programs__card__description">
                {render(item.description)}
              </div>
            ) : null}
          </div>

          {subItems.length > 0 ? (
            <ul className="programs__card__tags">
              {subItems.map((sub) => (
                <li
                  key={sub._uid}
                  {...storyblokEditable(sub)}
                  className="programs__card__tag"
                >
                  <div className="programs__card__tag__content">
                    {render(sub.content)}
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {item.cta?.length > 0 ? (
          <div className="programs__card__featured-cta">
            {item.cta.map((cta) => (
              <Button
                key={cta._uid}
                link={cta.link}
                theme={cta.theme || 'primary'}
                height="small"
                dangerouslySetInnerHTML={{
                  __html: markdownToHtml(cta.title),
                }}
              />
            ))}
          </div>
        ) : null}
      </div>

      {isImage(item.image) ? (
        <div className="programs__card__featured-image">
          <Image
            image={item.image}
            sizes="(min-width: 768px) 450px, 80vw"
          />
        </div>
      ) : null}
    </li>
  );
};

export const Programs = ({ blok }) => {
  const items = blok.items || [];

  return (
    <div {...storyblokEditable(blok)} className="programs">
      <MaxWidth>
        <div className="programs__container">
          {blok.supTitle ? (
            <div className="programs__sup-title">
              <span className="programs__sup-title__dot" aria-hidden="true" />

              <span className="programs__sup-title__text">
                {blok.supTitle}
              </span>
            </div>
          ) : null}

          {items.length > 0 ? (
            <ul className="programs__grid">
              {items.map((item) =>
                item.featured ? (
                  <ProgramsFeaturedCard key={item._uid} item={item} />
                ) : (
                  <ProgramsCard key={item._uid} item={item} />
                ),
              )}
            </ul>
          ) : null}
        </div>
      </MaxWidth>
    </div>
  );
};
